import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, query, limit, where, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Chart, registerables } from 'chart.js';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { getDateRange, formatDate, validateDateRange } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { monitorQuery, monitorParallelQueries } from '../utils/queryMonitoring';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import CompactChart from '../components/common/CompactChart';
import { useUserRole } from '../hooks/useUserRole';
import { usePermissions } from '../hooks/usePermissions';
import { canViewAllData, hasPermission, canViewAllReports, FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import { functions } from '../utils/firebase';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { exportData, exportMultiSectionData } from '../utils/exportUtils';
import { 
    calculateTopStyles, 
    calculateTopCollections, 
    calculateTotalItemRevenue,
    calculateTotalItems
} from '../utils/productAnalytics';
import { API_URL } from '../utils/apiConfig';

Chart.register(...registerables);

// Units for an order. Excludes misc charges (no size) and O/S sizes to match AM Order Analysis "Units".
// Cached orders carry a pre-calculated item_count; live orders need the line items summed.
const getOrderItemCount = (order) => {
    if (order.item_count) return order.item_count;
    if (!Array.isArray(order.order_items)) return 0;
    return order.order_items.reduce((sum, item) => {
        const sizeRaw = item.size || item.size_name || item.sizeName || item.Size;
        if (!sizeRaw) return sum;
        const s = String(sizeRaw).trim().toUpperCase();
        if (s === 'O/S' || s === 'O / S' || s === 'ONE SIZE') return sum;
        return sum + (parseFloat(item.qty) || 0);
    }, 0);
};

const OrderReportsPage = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [range, setRange] = useUrlState('range', "30");
    const [customStart, setCustomStart] = useUrlState('customStart', "");
    const [customEnd, setCustomEnd] = useUrlState('customEnd', "");
    const [selectedRep, setSelectedRep] = useUrlState('selectedRep', ''); // Single rep selection
    const [showRepDropdown, setShowRepDropdown] = useState(false);
    const [selectedcustomers, setSelectedcustomers] = useUrlState('selectedcustomers', []);
    const [customerSearch, setcustomerSearch] = useState('');
    const [showCustomerDropdown, setshowCustomerDropdown] = useState(false);
    
    // Pending filter state — all filters wait for Apply button
    const [pendingRange, setPendingRange] = useState(range || '30');
    const [pendingCustomStart, setPendingCustomStart] = useState(customStart || '');
    const [pendingCustomEnd, setPendingCustomEnd] = useState(customEnd || '');
    const [pendingRep, setPendingRep] = useState('');
    const [pendingcustomers, setPendingcustomers] = useState([]);
    const [filtersChanged, setFiltersChanged] = useState(false);
    
    const [usingCache, setUsingCache] = useState(false);
    const [cacheAge, setCacheAge] = useState(null);
    const [cachedData, setCachedData] = useState(null);
    const [repProductAnalytics, setRepProductAnalytics] = useState(null);
    const [loadingRepAnalytics, setLoadingRepAnalytics] = useState(false);
    const [rawOrdersData, setRawOrdersData] = useState(null); // Store raw orders for debugging
    const [allOrdersForChart, setAllOrdersForChart] = useState([]); // Full orders for time series chart
    const repDropdownRef = useRef(null);
    const customerDropdownRef = useRef(null);
    const allRepNamesRef = useRef(null); // Stores full rep list from initial unfiltered load
    
    // Chart configuration state
    const [orderStatusChartConfig, setOrderStatusChartConfig] = useState(null);
    const [orderDistributionChartConfig, setOrderDistributionChartConfig] = useState(null);

    // Close rep dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (repDropdownRef.current && !repDropdownRef.current.contains(event.target)) {
                setShowRepDropdown(false);
            }
        };

        if (showRepDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showRepDropdown]);

    // Close customer dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
                setshowCustomerDropdown(false);
            }
        };

        if (showCustomerDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showCustomerDropdown]);

    // Detect when pending filters differ from applied filters
    useEffect(() => {
        const repChanged = pendingRep !== selectedRep;
        const customerChanged = JSON.stringify(pendingcustomers) !== JSON.stringify(selectedcustomers);
        const dateChanged = pendingRange !== range || pendingCustomStart !== customStart || pendingCustomEnd !== customEnd;
        setFiltersChanged(repChanged || customerChanged || dateChanged);
    }, [pendingRep, pendingcustomers, selectedRep, selectedcustomers, pendingRange, range, pendingCustomStart, customStart, pendingCustomEnd, customEnd]);

    // Initialize pending filters when component mounts
    useEffect(() => {
        setPendingRep(selectedRep);
        setPendingcustomers(selectedcustomers);
    }, []);

    // Sync pending state from URL on external changes (browser back/forward)
    useEffect(() => { setPendingRange(range || '30'); }, [range]);
    useEffect(() => { setPendingCustomStart(customStart || ''); }, [customStart]);
    useEffect(() => { setPendingCustomEnd(customEnd || ''); }, [customEnd]);

    // Apply filter button handler — copies all pending state to URL (batched via microtask)
    const handleApplyFilters = () => {
        setRange(pendingRange);
        setCustomStart(pendingCustomStart);
        setCustomEnd(pendingCustomEnd);
        setSelectedRep(pendingRep);
        setSelectedcustomers(pendingcustomers);
        setFiltersChanged(false);
    };

    // Clear all filters — resets pending + URL state
    const handleClearFilters = () => {
        // Reset pending state
        setPendingRange('30');
        setPendingCustomStart('');
        setPendingCustomEnd('');
        setPendingRep('');
        setPendingcustomers([]);
        setcustomerSearch('');
        // Reset URL state (batched via microtask → single re-render)
        setRange('30');
        setCustomStart('');
        setCustomEnd('');
        setSelectedRep('');
        setSelectedcustomers([]);
        setFiltersChanged(false);
    };

    // Apply rep filter changes use fulfillment_status as authoritative source with fallback
    const getOrderStatus = useCallback((order) => {
        // Check for cancelled/returned first (from order.status field)
        if (order.status === 'Cancelled') return 'cancelled';
        
        // Use fulfillment_status as primary source if available
        if (order.fulfillment_status) {
            const status = order.fulfillment_status.toLowerCase();
            
            if (status === 'cancelled' || status === 'returned') {
                return 'cancelled';
            } else if (status === 'shipped' || status === 'delivered' || status === 'complete') {
                return 'shipped';
            } else if (status === 'partial' || status === 'partially shipped') {
                return 'partial';
            } else if (status === 'open' || status === 'pending' || status === 'picking') {
                // Not shipped statuses - check amount fields to distinguish partial
                const amountShipped = parseFloat(order.amount_shipped) || 0;
                const amountOpen = parseFloat(order.amount_open) || 0;
                
                if (amountShipped > 0 && amountOpen > 0) {
                    return 'partial';
                } else {
                    return 'open';
                }
            }
            // Unknown fulfillment_status - fall through to amount-based calculation
        }
        
        // Fallback to amount-based calculation if fulfillment_status not available or unknown
        const amountShipped = parseFloat(order.amount_shipped) || 0;
        const amountOpen = parseFloat(order.amount_open) || 0;
        
        // Fully Shipped: amount_open is 0 (nothing left to ship)
        if (amountOpen === 0 && amountShipped > 0) return 'shipped';
        
        // Partial Shipped: has shipped amount AND open amount
        if (amountShipped > 0 && amountOpen > 0) return 'partial';
        
        // Not Shipped: nothing shipped yet (or new order with both at 0)
        return 'open';
    }, []);

    useEffect(() => {
        if (!user || !user.uid) return;
        if (userRole === undefined || userRole === null) return; // Wait for role to load

        const fetchData = async () => {
            setLoading(true);
            setRepProductAnalytics(null); // Clear stale API analytics on every fetch
            try {
                console.log('[OrderReports] Fetching with userRole:', userRole);

                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }

                // Get date range
                const { start, end } = getDateRange(range, customStart, customEnd);
                const isStandardRange = ['7','14', '30', '90', '180', '365', 'ytd'].includes(range);
                const hasActiveFilters = selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0]);

                // Cache stores only TOP 100 orders by $ amount (Firestore 1MB doc limit).
                // Non-admin users MUST bypass cache — filtering the top-100 list drops their
                // smaller orders and produces inaccurate totals that don't match SalesRepReports
                // (which uses pre-aggregated repPerformance).
                const canUseCache = canViewAllReports(user) || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);

                console.log('[OrderReports] Cache decision:', {
                    isStandardRange,
                    hasActiveFilters,
                    selectedRep,
                    selectedcustomers: selectedcustomers[0] || 'none',
                    canUseCache,
                    willUseCache: isStandardRange && !hasActiveFilters && canUseCache
                });

                // Try to use cached data ONLY for standard ranges with NO filters AND admin/view-all users
                if (isStandardRange && !hasActiveFilters && canUseCache) {
                    const cacheId = range === 'ytd' 
                        ? 'summary_ytd' 
                        : `summary_${range.padStart(2, '0')}d`; // Pad with zero (e.g., '7' -> '07d')

                    try {
                        const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), cacheId));
                        
                        if (cacheDoc.exists()) {
                            const data = cacheDoc.data();
                            const now = new Date();
                            const lastUpdated = data.lastUpdated?.toDate?.() || new Date(0);
                            const ageMinutes = Math.floor((now - lastUpdated) / (1000 * 60));
                            
                            // Use cache (warn if older than 4 hours but still use it)
                            const cacheIsStale = ageMinutes >= 240;
                            if (cacheIsStale) {
                                console.warn(`[OrderReports] Cache is ${ageMinutes} minutes old (stale), but using it anyway`);
                            }
                            
                            setCachedData(data);
                            setUsingCache(true);
                            setCacheAge(ageMinutes);
                            
                            // Check if cache has ordersForRepFilter (top 100 orders by amount)
                            // Note: Cache only stores top 100 to stay under Firestore 1MB limit
                            let cachedOrders = data.ordersForRepFilter || [];
                            
                            console.log('[OrderReports] 🔍 DEBUG CACHE:', {
                                totalCachedOrders: cachedOrders.length,
                                cacheNote: 'Top 100 orders by $ amount only',
                                userRole,
                                userDisplayName: user?.displayName,
                                isAdmin: canViewAllData(user),
                                willFilterRBAC: !canViewAllData(user) && user?.displayName,
                                sampleOrder: cachedOrders[0],
                                sampleFields: cachedOrders[0] ? Object.keys(cachedOrders[0]) : []
                            });
                            
                            // RBAC: Non-admin users only see orders they're assigned to
                            // Wait for userRole to load before filtering
                            if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && user?.displayName) {
                                const matchNames = getRbacMatchNames(user);
                                const matchSet = new Set(matchNames);
                                const beforeFilter = cachedOrders.length;
                                cachedOrders = cachedOrders.filter(order =>
                                    order.commissions_displayNames?.some(name => matchSet.has(name.toLowerCase().trim()))
                                );
                            }
                            
                            setOrders(cachedOrders);
                            
                            // Store rep names from cache for dropdown (unfiltered source)
                            if (!allRepNamesRef.current) {
                                const repSet = new Set();
                                cachedOrders.forEach(order => {
                                    if (Array.isArray(order.commissions_displayNames)) {
                                        order.commissions_displayNames.forEach(name => {
                                            if (name && name.trim()) repSet.add(name.trim());
                                        });
                                    }
                                });
                                allRepNamesRef.current = Array.from(repSet).sort();
                            }
                            
                            // CRITICAL FIX: Recalculate product analytics from RBAC-filtered orders
                            // The cached analytics were calculated from ALL orders (admin view)
                            // but non-admin users need analytics from THEIR orders only
                            if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && cachedOrders.length > 0) {
                                const topStyles = calculateTopStyles(cachedOrders);
                                const topCollections = calculateTopCollections(cachedOrders);
                                
                                // Override cached analytics with recalculated data
                                setCachedData({
                                    ...data,
                                    topStyles,
                                    topCollections
                                });
                            } else {
                                // Admin users can use pre-calculated cache analytics
                                // Backward compat: old cache uses topColors, new uses topCollections
                                setCachedData({
                                    ...data,
                                    topCollections: data.topCollections || data.topColors
                                });
                            }
                            
                            // Fetch users and customers in parallel for dropdowns
                            // Only fetch minimal fields needed for filters
                            const [usersSnap, customersSnap] = await Promise.all([
                                getDocs(query(
                                    collection(db, collectionPath('users')),
                                    limit(500) // Users are typically < 100
                                )),
                                getDocs(query(
                                    collection(db, collectionPath('customers')),
                                    limit(2000) // Reduced from 5000
                                ))
                            ]);
                            
                            const usersData = usersSnap.docs.map(doc => ({
                                uid: doc.id,
                                displayName: doc.data().displayName,
                                email: doc.data().email,
                                role: doc.data().role,
                                hideFromReports: doc.data().hideFromReports || false
                            }));
                            setUsers(usersData);
                            
                            const customersData = customersSnap.docs.map(doc => ({
                                id: doc.id,
                                customerName: doc.data().customerName,
                                apparelMagicId: doc.data().apparelMagicId,
                                customerAccountNumber: doc.data().customerAccountNumber,
                                salesRepNames: doc.data().salesRepNames || []
                            }));
                            setCustomers(customersData);
                            
                            // Note: Cache only has top 100 orders, so line chart will show 
                            // limited data. For full accuracy, use "Refresh Cache" or apply filters.
                            // The cached orders are used for the time series chart.
                            setAllOrdersForChart(cachedOrders);
                            
                            setLoading(false);
                            return;
                        }
                    } catch (cacheErr) {
                        console.error('[OrderReports] Cache fetch error:', cacheErr);
                        // Cache fetch failed, fall back to live data
                    }
                    
                    console.log('[OrderReports] Cache not available or failed, fetching live data');
                }

                // If filters are active OR non-standard range (custom), fetch from API instead of Firestore
                if (hasActiveFilters || !isStandardRange) {
                    console.log(`[OrderReports] Using API - filters: ${hasActiveFilters}, customRange: ${!isStandardRange}`);
                    setUsingCache(false);
                    setCacheAge(null);
                    setCachedData(null);
                    
                    // Still fetch users and customers for dropdowns (minimal fields only)
                    const [usersSnap, customersSnap] = await monitorParallelQueries([
                        {
                            name: 'fetch-users-order-reports',
                            fn: () => getDocs(query(
                                collection(db, collectionPath('users')),
                                limit(500)
                            ))
                        },
                        {
                            name: 'fetch-customers-order-reports',
                            fn: () => getDocs(query(
                                collection(db, collectionPath('customers')),
                                limit(2000)
                            ))
                        }
                    ]);
                    
                    const usersData = usersSnap.docs.map(doc => ({
                        uid: doc.id,
                        displayName: doc.data().displayName,
                        email: doc.data().email,
                        role: doc.data().role,
                        hideFromReports: doc.data().hideFromReports || false
                    }));
                    setUsers(usersData);
                    
                    const customersData = customersSnap.docs.map(doc => ({
                        id: doc.id,
                        customerName: doc.data().customerName,
                        apparelMagicId: doc.data().apparelMagicId,
                        customerAccountNumber: doc.data().customerAccountNumber,
                        salesRepNames: doc.data().salesRepNames || []
                    }));
                    setCustomers(customersData);
                    
                    // Fetch product analytics from API
                    setLoadingRepAnalytics(true);
                    
                    try {
                        console.log(`[Product Analytics] Fetching from API - Rep: ${selectedRep || 'all'}, customer: ${selectedcustomers[0] || 'all'}`);
                        
                        // Send YYYY-MM-DD strings to avoid timezone day-shift
                        // (toISOString() converts local midnight to UTC which can cross day boundaries)
                        const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
                        const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
                        const requestBody = {
                            dateRange: {
                                start: startStr,
                                end: endStr
                            }
                        };
                        
                        // Add rep filter if selected
                        if (selectedRep) {
                            requestBody.salesReps = [selectedRep];
                        }
                        
                        // Add Customer Filter if one is selected
                        if (selectedcustomers.length > 0 && selectedcustomers[0]) {
                            requestBody.customerId = selectedcustomers[0];
                        }
                        
                        const response = await fetch(`${API_URL}/api/orders/product-analytics`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(requestBody)
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }

                        const data = await response.json();
                        
                        if (!data.success) {
                            throw new Error(data.error || 'Failed to fetch product analytics');
                        }

                        // Store raw orders AND populate orders state for cards
                        // RBAC: Filter API response for non-admin users
                        let apiOrders = data.orders;
                        if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && user?.displayName) {
                            const matchNames = getRbacMatchNames(user);
                            const matchSet = new Set(matchNames);
                            apiOrders = apiOrders.filter(order =>
                                order.commissions_displayNames?.some(name => matchSet.has(name.toLowerCase().trim()))
                            );
                            console.log(`[OrderReports] RBAC filtered API response: ${data.orders.length} → ${apiOrders.length}`);
                        }
                        setRawOrdersData(apiOrders);
                        setOrders(apiOrders);

                        // Calculate product analytics from order_items
                        const topStyles = calculateTopStyles(apiOrders);
                        const topCollections = calculateTopCollections(apiOrders);
                        // Calculate total item revenue for consistency with card display
                        const totalItemRevenue = calculateTotalItemRevenue(apiOrders);

                        setRepProductAnalytics({
                            topStyles,
                            topCollections,
                            totalItemRevenue
                        });

                        setLoadingRepAnalytics(false);
                    } catch (error) {
                        console.error('[Product Analytics] Error:', error);
                        
                        // Fallback: fetch orders directly from Firestore when API is unavailable
                        console.log('[OrderReports] API unavailable, falling back to Firestore query');
                        try {
                            const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
                            const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
                            
                            const ordersSnap = await getDocs(query(
                                collection(db, collectionPath('orders')),
                                where('date_internal', '>=', startStr),
                                where('date_internal', '<=', endStr),
                                limit(5000)
                            ));
                            
                            const fallbackOrders = ordersSnap.docs.map(d => ({
                                id: d.id,
                                ...d.data()
                            }));
                            
                            console.log(`[OrderReports] Firestore fallback loaded ${fallbackOrders.length} orders`);
                            setOrders(fallbackOrders);
                            setRawOrdersData(fallbackOrders);
                            setAllOrdersForChart(fallbackOrders);
                            
                            // Calculate product analytics from fallback data
                            const topStyles = calculateTopStyles(fallbackOrders);
                            const topCollections = calculateTopCollections(fallbackOrders);
                            const totalItemRevenue = calculateTotalItemRevenue(fallbackOrders);
                            
                            setRepProductAnalytics({
                                topStyles,
                                topCollections,
                                totalItemRevenue
                            });
                        } catch (fallbackError) {
                            console.error('[OrderReports] Firestore fallback also failed:', fallbackError);
                            setToast({ 
                                message: `Failed to load orders: ${error.message}`, 
                                type: 'error' 
                            });
                            setRepProductAnalytics(null);
                            setRawOrdersData(null);
                        }
                        
                        setLoadingRepAnalytics(false);
                    }
                    
                    setLoading(false);
                    return;
                }

                // If we get here, need to fetch live data from Firestore (no filters, no cache)
                setUsingCache(false);
                setCacheAge(null);
                setCachedData(null);

                // BEST PRACTICE: Use parallel async operations with monitoring to track performance
                // Fetch orders and users in parallel (no data dependency).
                // Always use date_internal indexed range query — fetching limit(5000) without
                // a date filter returns an unordered subset and misses recent orders.
                const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                const startStr = toDateStr(start);
                const endStr   = toDateStr(end);
                const [ordersSnap, usersSnap] = await monitorParallelQueries([
                    {
                        name: 'fetch-orders-order-reports',
                        fn: () => getDocs(query(
                            collection(db, collectionPath('orders')),
                            where('date_internal', '>=', startStr),
                            where('date_internal', '<=', endStr),
                            orderBy('date_internal', 'desc'),
                            limit(5000)
                        ))
                    },
                    {
                        name: 'fetch-users-order-reports',
                        fn: () => getDocs(query(
                            collection(db, collectionPath('users')),
                            limit(500)
                        ))
                    }
                ]);
                
                // Only extract fields needed for reports
                let ordersData = ordersSnap.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        creation_time: data.creation_time,
                        createdAt: data.createdAt,
                        date_internal: data.date_internal,
                        fulfillment_status: data.fulfillment_status,
                        status: data.status,
                        amount: data.amount,
                        total: data.total,
                        amount_shipped: data.amount_shipped,
                        amount_open: data.amount_open,
                        customer_id: data.customer_id,
                        customer_name: data.customer_name,
                        order_id: data.order_id,
                        commissions_displayNames: data.commissions_displayNames,
                        order_items: data.order_items, // Needed for product analytics
                        is_quote: data.is_quote || 0
                    };
                });
                
                // RBAC: Non-admin users only see orders they're assigned to
                // Wait for userRole to load before filtering
                // Case-insensitive multi-name matching (aligns with cache-path filter above).
                if (!canViewAllReports(user, FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && user?.displayName) {
                    const matchSet = new Set(getRbacMatchNames(user));
                    const beforeFilter = ordersData.length;
                    ordersData = ordersData.filter(order =>
                        order.commissions_displayNames?.some(name => matchSet.has(String(name || '').toLowerCase().trim()))
                    );
                }
                
                // Store rep names from unfiltered orders for dropdown
                if (!allRepNamesRef.current) {
                    const repSet = new Set();
                    ordersData.forEach(order => {
                        if (Array.isArray(order.commissions_displayNames)) {
                            order.commissions_displayNames.forEach(name => {
                                if (name && name.trim()) repSet.add(name.trim());
                            });
                        }
                    });
                    allRepNamesRef.current = Array.from(repSet).sort();
                }
                
                setOrders(ordersData);

                // Compute Top Styles / Top Collections directly from the RBAC-filtered
                // orders. The cache and API code paths populate repProductAnalytics for
                // admin flows; the live-fetch path (non-admin bypassing the cache) must do
                // the same so the analytics panels are not empty. Match the API/fallback
                // paths (lines ~470, ~509) which pass the full order list \u2014 do NOT pre-filter
                // by !order.is_quote because ApparelMagic may store the flag as the string
                // "0", which is truthy in JS and would silently drop every real order.
                try {
                    const topStyles = calculateTopStyles(ordersData);
                    const topCollections = calculateTopCollections(ordersData);
                    const totalItemRevenue = calculateTotalItemRevenue(ordersData);
                    setRepProductAnalytics({
                        topStyles,
                        topCollections,
                        totalItemRevenue
                    });
                } catch (analyticsErr) {
                    console.warn('[OrderReports] Failed to compute product analytics from live orders:', analyticsErr);
                }
                
                const usersData = usersSnap.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                }));
                setUsers(usersData);

                setLoading(false);
            } catch (error) {
                console.error('[OrderReports] Error fetching data:', error);
                setToast({ message: `Error loading data: ${error.message}`, type: 'error' });
                setLoading(false);
            }
        };

        fetchData();
    }, [user, range, customStart, customEnd, selectedRep, selectedcustomers, userRole, can]); // Combined dependencies

    // Handle cache refresh (admin only)
    const [cacheRefreshing, setCacheRefreshing] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(0);
    const refreshTimerRef = useRef(null);

    const handleRefreshCache = async () => {
        if (!canViewAllData(user)) {
            setToast({ message: 'Only admins can refresh the cache', type: 'error' });
            return;
        }

        const confirmed = window.confirm(
            'This will refresh the reports cache for all date ranges. ' +
            'It may take 1-2 minutes. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setCacheRefreshing(true);
            setRefreshProgress(0);
            
            // Start progress timer (simulate progress)
            let progress = 0;
            refreshTimerRef.current = setInterval(() => {
                progress += 1;
                if (progress <= 90) { // Cap at 90% until actual completion
                    setRefreshProgress(progress);
                }
            }, 1000); // Update every second
            
            setToast({ message: 'Refreshing cache... Please wait.', type: 'success' });
            
            const refreshCacheFunc = httpsCallable(functions, 'refreshReportsCache', { timeout: 540000 });
            const result = await refreshCacheFunc();
            
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            setRefreshProgress(100);
            
            setToast({ message: 'Cache refreshed successfully! Reloading data...', type: 'success' });
            
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            console.error('[OrderReports] Error refreshing cache:', error);
            console.error('[OrderReports] Error details:', {
                code: error.code,
                message: error.message,
                details: error.details
            });
            
            setCacheRefreshing(false);
            setRefreshProgress(0);
            
            let errorMessage = 'Error refreshing cache';
            if (error.code === 'unauthenticated') {
                errorMessage = 'Please sign in to refresh the cache';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'Only admins can refresh the cache';
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    // Cleanup progress timer on unmount
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, []);

    // Date range filtering
    const { start: filterStart, end: filterEnd } = useMemo(() => 
        getDateRange(range, customStart, customEnd), 
        [range, customStart, customEnd]
    );

    // Filter orders by date range
    const filteredOrders = useMemo(() => {
        // If filters are active and we have orders from API, skip date filtering (API already filtered by date)
        const hasActiveFilters = selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0]);
        if (hasActiveFilters && orders.length > 0 && !usingCache) {
            console.log('[Date Filter] Skipping - API data already date-filtered:', orders.length, 'orders');
            return orders; // API already filtered by date range
        }
        
        // Otherwise, filter by date range (cache or Firestore data)
        // Use YYYY-MM-DD string comparison to avoid timezone issues.
        // date_internal is a date-only field (no time), so comparing as strings is correct
        // and avoids the bug where new Date('YYYY-MM-DD') (UTC midnight) fails comparison
        // with new Date(year, month, day) (local midnight, which is UTC+offset).
        const startStr = `${filterStart.getFullYear()}-${String(filterStart.getMonth() + 1).padStart(2, '0')}-${String(filterStart.getDate()).padStart(2, '0')}`;
        const endStr = `${filterEnd.getFullYear()}-${String(filterEnd.getMonth() + 1).padStart(2, '0')}-${String(filterEnd.getDate()).padStart(2, '0')}`;
        
        return orders.filter(order => {
            if (order.date_internal) {
                return order.date_internal >= startStr && order.date_internal <= endStr;
            }
            // Fallback for orders without date_internal (legacy)
            const orderDate = order.creation_time 
                ? new Date(order.creation_time.replace(' ', 'T'))
                : order.createdAt?.toDate?.() 
                    ? order.createdAt.toDate() 
                    : null;
            
            if (!orderDate) return false;
            return orderDate >= filterStart && orderDate <= filterEnd;
        });
    }, [orders, filterStart, filterEnd, selectedRep, selectedcustomers, usingCache]);

    // Filter by selected rep
    const filteredOrdersByRep = useMemo(() => {
        // If filters are active and we have orders, they're already filtered by API - skip client-side filtering
        const hasActiveFilters = selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0]);
        if (hasActiveFilters && orders.length > 0 && !usingCache) {
            console.log('[Rep Filter] Skipping - API data already rep-filtered:', filteredOrders.length, 'orders');
            return filteredOrders; // API already filtered by rep
        }
        
        // Otherwise do client-side filtering (cache or no filters)
        if (!selectedRep) return filteredOrders;

        return filteredOrders.filter(order => {
            if (!Array.isArray(order.commissions_displayNames)) return false;
            return order.commissions_displayNames.includes(selectedRep);
        });
    }, [filteredOrders, selectedRep, selectedcustomers, orders.length, usingCache]);

    // Filter by selected customer (applies after rep filter)
    const filteredOrdersByCustomer = useMemo(() => {
        // If filters are active and we have orders, they're already filtered by API - skip client-side filtering
        const hasActiveFilters = selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0]);
        if (hasActiveFilters && orders.length > 0 && !usingCache) {
            console.log('[Customer Filter] Skipping - API data already customer-filtered:', filteredOrdersByRep.length, 'orders');
            return filteredOrdersByRep; // API already filtered by customer
        }
        
        // Otherwise do client-side filtering (cache or no filters)
        if (selectedcustomers.length === 0 || !selectedcustomers[0]) return filteredOrdersByRep;

        return filteredOrdersByRep.filter(order => {
            return String(order.customer_id) === String(selectedcustomers[0]);
        });
    }, [filteredOrdersByRep, selectedcustomers, selectedRep, orders.length, usingCache]);

    // Helper: check if an order is a quote (handles string, number, and boolean values)
    const isQuoteOrder = useCallback((order) => {
        const v = order.is_quote;
        return v === 1 || v === '1' || v === true || parseInt(v) === 1;
    }, []);

    // Separate quotes from regular orders
    const nonQuoteOrders = useMemo(() => {
        const result = filteredOrdersByCustomer.filter(order => !isQuoteOrder(order));
        const quotes = filteredOrdersByCustomer.length - result.length;
        if (quotes > 0 || filteredOrdersByCustomer.length > 0) {
            console.log('[Quote Split] Total:', filteredOrdersByCustomer.length, '| Orders:', result.length, '| Quotes:', quotes);
        }
        return result;
    }, [filteredOrdersByCustomer, isQuoteOrder]);

    const quoteOrders = useMemo(() => {
        return filteredOrdersByCustomer.filter(order => isQuoteOrder(order));
    }, [filteredOrdersByCustomer, isQuoteOrder]);

    // Quote metrics - prefer cache summary if available, else calculate from quoteOrders
    const quoteMetrics = useMemo(() => {
        const noFilters = !selectedRep && (!selectedcustomers[0]);
        if (usingCache && cachedData?.summary && noFilters && canViewAllData(user)) {
            return {
                count: cachedData.summary.quoteCount || 0,
                totalRevenue: cachedData.summary.quoteRevenue || 0
            };
        }
        const totalQuoteRevenue = quoteOrders.reduce(
            (sum, order) => sum + parseFloat(order.total || order.amount || 0), 0
        );
        return {
            count: quoteOrders.length,
            totalRevenue: totalQuoteRevenue
        };
    }, [quoteOrders, usingCache, cachedData, selectedRep, selectedcustomers, user]);

    // Recent orders from current date filter (top 10 by amount, memoized)
    // Includes ALL orders (quotes + non-quotes) so quotes appear with badge
    const recentOrders = useMemo(() => {
        return [...filteredOrdersByCustomer]
            .sort((a, b) => {
                // Cache uses 'total', live data uses 'amount'
                const amountA = a.total || a.amount || 0;
                const amountB = b.total || b.amount || 0;
                return amountB - amountA;
            })
            .slice(0, 10);
    }, [filteredOrdersByCustomer]);

    // Build set of rep names to hide (hideFromReports flag on user docs)
    const hiddenRepNames = useMemo(() => {
        const names = new Set();
        users.forEach(u => {
            if (u.hideFromReports && u.displayName) {
                names.add(u.displayName);
            }
        });
        return names;
    }, [users]);

    // Get list of all reps for dropdown - uses ref populated on first load
    // This ensures dropdown always shows all reps regardless of current filters
    const allReps = useMemo(() => {
        // Use stored rep names from initial unfiltered load
        if (allRepNamesRef.current) {
            return allRepNamesRef.current
                .filter(name => !hiddenRepNames.has(name))
                .map(name => ({ name }));
        }
        
        // Fallback: build from current orders (initial load before ref is set)
        const repSet = new Set();
        orders.forEach(order => {
            if (Array.isArray(order.commissions_displayNames)) {
                order.commissions_displayNames.forEach(name => {
                    if (name && name.trim() && !hiddenRepNames.has(name.trim())) {
                        repSet.add(name.trim());
                    }
                });
            }
        });
        return Array.from(repSet).sort().map(name => ({ name }));
    }, [orders, hiddenRepNames]);

    // Get unique customers - parent-child with Sales Rep filter
    // Admins (no rep): all customers | Rep selected: that rep's customers | Non-admin (no rep): their own customers
    const availablecustomers = useMemo(() => {
        const activeRep = pendingRep || selectedRep;
        
        // When a rep is selected, show only that rep's customers (parent-child)
        if (activeRep) {
            const repCustomers = customers
                .filter(customer => {
                    return Array.isArray(customer.salesRepNames) && 
                           customer.salesRepNames.includes(activeRep);
                })
                .map(customer => ({
                    id: customer.apparelMagicId || customer.customerAccountNumber,
                    name: customer.customerName || 'Unknown Customer'
                }))
                .filter(c => c.id)
                .sort((a, b) => a.name.localeCompare(b.name));
            return repCustomers;
        }
        
        // No rep selected - show customers based on RBAC
        if (canViewAllData(user)) {
            // Admins see all customers
            return customers
                .map(customer => ({
                    id: customer.apparelMagicId || customer.customerAccountNumber,
                    name: customer.customerName || 'Unknown Customer'
                }))
                .filter(c => c.id)
                .sort((a, b) => a.name.localeCompare(b.name));
        }
        
        // Non-admin: show only their assigned customers
        const displayName = user?.displayName;
        if (!displayName) return [];
        return customers
            .filter(customer => {
                return Array.isArray(customer.salesRepNames) && 
                       customer.salesRepNames.includes(displayName);
            })
            .map(customer => ({
                id: customer.apparelMagicId || customer.customerAccountNumber,
                name: customer.customerName || 'Unknown Customer'
            }))
            .filter(c => c.id)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [customers, selectedRep, pendingRep, user]);

    // Filter customers by search term
    const filteredcustomers = useMemo(() => {
        if (!customerSearch.trim()) return availablecustomers;
        
        const searchLower = customerSearch.toLowerCase().trim();
        return availablecustomers.filter(customer => 
            customer.name.toLowerCase().includes(searchLower)
        );
    }, [availablecustomers, customerSearch]);

    // Calculate order metrics
    const orderMetrics = useMemo(() => {
        // Use cached summary only if: using cache, no filters active, and admin
        const noFilters = !selectedRep && (!selectedcustomers[0]);
        
        if (usingCache && cachedData && cachedData.summary && noFilters && canViewAllData(user)) {
            const summary = cachedData.summary;
            
            // Use totalItemRevenue from cache (calculated by cloud function)
            // This ensures consistency with product analytics tables (topSizes, topStyles, etc.)
            // Falls back to totalRevenue if totalItemRevenue not available (old cache format)
            const totalItemRevenueCached = summary.totalItemRevenue || 0;
            const totalRevenueCached = summary.totalRevenue || 0;
            const displayRevenue = totalItemRevenueCached > 0 
                ? totalItemRevenueCached 
                : totalRevenueCached;
            
            return {
                total: summary.totalOrders || 0,
                totalRevenue: totalRevenueCached,
                totalValue: displayRevenue,
                avgOrderValue: summary.totalOrders > 0 ? (displayRevenue / summary.totalOrders) : 0,
                totalItems: summary.totalItems || 0,
                byStatus: summary.orderStatusCounts || { open: 0, partial: 0, shipped: 0, cancelled: 0 },
                totalOpenOrders: summary.totalOpenOrders || 0
            };
        }
        
        // Calculate from non-quote orders when any filter is active
        const statusCounts = {
            open: 0,      // Not shipped yet
            partial: 0,   // Partially shipped
            shipped: 0,   // Fully shipped
            cancelled: 0
        };
        
        nonQuoteOrders.forEach(order => {
            const status = getOrderStatus(order);
            if (statusCounts.hasOwnProperty(status)) {
                statusCounts[status]++;
            }
        });
        
        // Calculate total open orders (not fully shipped = open + partial)
        const totalOpenOrders = statusCounts.open + statusCounts.partial;

        // Calculate everything from live data
        // Use consolidated calculateTotalItemRevenue from productAnalytics.js
        // This ensures the card matches the product analytics tables exactly
        const totalItemRevenue = calculateTotalItemRevenue(nonQuoteOrders);
        
        
        // Calculate full order totals for reference
        const totalOrderValue = nonQuoteOrders.reduce(
            (sum, order) => sum + parseFloat(order.total || order.amount || 0), 0
        );

        // Use item-level revenue for display (matches product tables)
        // If no order_items data available, fall back to order total
        const displayValue = totalItemRevenue > 0 ? totalItemRevenue : totalOrderValue;

        // Total item units (excludes misc charges)
        const totalItems = calculateTotalItems(nonQuoteOrders);

        return {
            total: nonQuoteOrders.length,
            totalRevenue: totalOrderValue,
            totalValue: displayValue,
            avgOrderValue: nonQuoteOrders.length > 0 ? displayValue / nonQuoteOrders.length : 0,
            totalItems,
            byStatus: statusCounts,
            totalOpenOrders
        };
    }, [nonQuoteOrders, usingCache, cachedData, selectedRep, selectedcustomers, getOrderStatus, userRole]);

    // Aggregate for every order ranked outside the top 10. Derived by subtracting the top 10
    // from the page totals rather than summing the leftover orders — in cache mode the orders
    // array only holds the cached top 100, which would cap this at 90.
    // Quotes are excluded so this reconciles with the summary cards.
    const topOrdersOtherSummary = useMemo(() => {
        const topNonQuote = recentOrders.filter(order => !isQuoteOrder(order));
        const otherCount = (orderMetrics.total || 0) - topNonQuote.length;
        if (otherCount <= 0) return null;

        const topAmount = topNonQuote.reduce((sum, o) => sum + (o.total || o.amount || 0), 0);
        const topItems = topNonQuote.reduce((sum, o) => sum + getOrderItemCount(o), 0);

        return {
            orderCount: otherCount,
            amount: Math.max(0, (orderMetrics.totalRevenue || 0) - topAmount),
            itemCount: Math.max(0, (orderMetrics.totalItems || 0) - topItems)
        };
    }, [recentOrders, orderMetrics, isQuoteOrder]);

    // Order status over time (daily or monthly depending on range)
    const orderStatusTimeSeries = useMemo(() => {
        // OPTIMIZATION: Use pre-calculated time series from cache when:
        // 1. Cache is active AND
        // 2. No rep filter is applied AND
        // 3. No Customer Filter is applied AND
        // 4. User is admin (non-admins need RBAC-filtered data recalculated)
        // This avoids client-side calculation of 260+ orders
        const hasRepFilter = selectedRep && selectedRep !== 'all';
        const hascustomerFilter = selectedcustomers && selectedcustomers.length > 0;
        const isAdmin = canViewAllData(user);
        
        // CRITICAL FIX: Non-admin users should NOT see cached time series data
        // They should see empty chart since they have no assigned orders in many cases
        if (usingCache && cachedData?.orderStatusTimeSeries && !hasRepFilter && !hascustomerFilter && isAdmin) {
            console.log('[Order Status Time Series] Using pre-calculated cache data:', {
                labels: cachedData.orderStatusTimeSeries.labels?.length,
                totals: cachedData.orderStatusTimeSeries.totals
            });
            return cachedData.orderStatusTimeSeries;
        }
        
        // Non-admin users: if they have no orders (nonQuoteOrders is empty),
        // return null to prevent showing cached data
        if (!isAdmin && nonQuoteOrders.length === 0) {
            console.log('[Order Status Time Series] Non-admin user with no assigned orders');
            return null;
        }
        
        // Fall back to client-side calculation when filters are applied OR user is non-admin (RBAC)
        // Use allOrdersForChart when cache is active (has all orders with minimal fields)
        // Fall back to nonQuoteOrders for live data mode
        const ordersForChart = usingCache && allOrdersForChart.length > 0 
            ? allOrdersForChart 
            : nonQuoteOrders;
        
        // Apply rep filter if needed (when using cached orders)
        const filteredOrders = hasRepFilter 
            ? ordersForChart.filter(o => o.commissions_displayNames?.includes(selectedRep))
            : ordersForChart;
        
        // Apply Customer Filter if needed
        // selectedcustomers contains apparelMagicId values directly (e.g., ['1011'])
        // orders have customer_id which is also the apparelMagicId
        const finalOrders = hascustomerFilter
            ? filteredOrders.filter(o => selectedcustomers.includes(String(o.customer_id)))
            : filteredOrders;
        
        console.log('[Order Status Time Series] Calculating from orders:', finalOrders.length, '(hasRepFilter:', hasRepFilter, ', hascustomerFilter:', hascustomerFilter, ')');
        
        const rangeDays = Math.ceil((filterEnd - filterStart) / (1000 * 60 * 60 * 24));
        const useMonthly = rangeDays > 90;

        // Generate all dates in the range first
        const dateMap = new Map();
        
        if (useMonthly) {
            const current = new Date(filterStart.getFullYear(), filterStart.getMonth(), 1);
            const endMonth = new Date(filterEnd.getFullYear(), filterEnd.getMonth(), 1);
            while (current <= endMonth) {
                const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
                dateMap.set(dateKey, { total: 0, open: 0, partial: 0, shipped: 0, cancelled: 0 });
                current.setMonth(current.getMonth() + 1);
            }
        } else {
            const current = new Date(filterStart);
            current.setHours(0, 0, 0, 0);
            const end = new Date(filterEnd);
            end.setHours(23, 59, 59, 999);
            while (current <= end) {
                const dateKey = current.toISOString().split('T')[0];
                dateMap.set(dateKey, { total: 0, open: 0, partial: 0, shipped: 0, cancelled: 0 });
                current.setDate(current.getDate() + 1);
            }
        }

        // Populate with order data
        // Use date_internal for chart to match filter (order scheduled date, not creation date)
        finalOrders.forEach(order => {
            const orderDate = order.date_internal
                ? (() => { const [y,m,d] = order.date_internal.split('-').map(Number); return new Date(y, m-1, d); })()
                : order.creation_time 
                    ? new Date(order.creation_time.replace(' ', 'T'))
                    : order.createdAt?.toDate?.() 
                        ? order.createdAt.toDate() 
                        : null;
            
            if (!orderDate) return;

            const dateKey = useMonthly
                ? `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`
                : orderDate.toISOString().split('T')[0];

            if (dateMap.has(dateKey)) {
                const data = dateMap.get(dateKey);
                data.total++;
                const status = getOrderStatus(order);
                if (data.hasOwnProperty(status)) {
                    data[status]++;
                } else {
                    data.open++;
                }
            }
        });

        // Sort dates and create arrays
        const sortedDates = Array.from(dateMap.keys()).sort();
        const labels = sortedDates.map(date => {
            if (useMonthly) {
                const [year, month] = date.split('-');
                return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }
            // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
            const [y, m, d] = date.split('-').map(Number);
            return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        const totalData = sortedDates.map(date => dateMap.get(date).total);
        const partialData = sortedDates.map(date => dateMap.get(date).partial);
        // Not Shipped should only include truly open orders, NOT partial (partial is shown separately)
        const notShippedData = sortedDates.map(date => dateMap.get(date).open);
        const shippedData = sortedDates.map(date => dateMap.get(date).shipped);
        const cancelledData = sortedDates.map(date => dateMap.get(date).cancelled);

        const totals = {
            total: totalData.reduce((a, b) => a + b, 0),
            notShipped: notShippedData.reduce((a, b) => a + b, 0),
            partial: partialData.reduce((a, b) => a + b, 0),
            shipped: shippedData.reduce((a, b) => a + b, 0),
            cancelled: cancelledData.reduce((a, b) => a + b, 0)
        };

        console.log('[Order Status Time Series] Generated data:', { dateCount: sortedDates.length, totals });

        return {
            labels,
            datasets: [
                {
                    label: 'Total Orders',
                    data: totalData,
                    borderColor: '#3B82F6',
                    backgroundColor: '#3B82F620',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                },
                {
                    label: 'Not Shipped',
                    data: notShippedData,
                    borderColor: '#F97316',
                    backgroundColor: '#F9731620',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                },
                {
                    label: 'Partial Shipped',
                    data: partialData,
                    borderColor: '#F59E0B',
                    backgroundColor: '#F59E0B20',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                },
                {
                    label: 'Fully Shipped',
                    data: shippedData,
                    borderColor: '#10B981',
                    backgroundColor: '#10B98120',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                },
                {
                    label: 'Cancelled',
                    data: cancelledData,
                    borderColor: '#EF4444',
                    backgroundColor: '#EF444420',
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                }
            ],
            totals
        };
    }, [nonQuoteOrders, allOrdersForChart, usingCache, cachedData, filterStart, filterEnd, getOrderStatus, selectedRep, selectedcustomers, customers, userRole]);

    // Prepare order status chart configuration
    useEffect(() => {
        // Check if we have time series data
        if (!orderStatusTimeSeries || !orderStatusTimeSeries.datasets) {
            console.log('[Chart Config] No time series data');
            setOrderStatusChartConfig(null);
            return;
        }

        // Check if we have labels (dates) - data can have zeros
        if (!orderStatusTimeSeries.labels || orderStatusTimeSeries.labels.length === 0) {
            console.log('[Chart Config] No labels (dates) available');
            setOrderStatusChartConfig(null);
            return;
        }

        console.log('[Chart Config] Creating chart with data:', {
            labels: orderStatusTimeSeries.labels?.length,
            datasets: orderStatusTimeSeries.datasets.length
        });

        setOrderStatusChartConfig({
            type: 'line',
            data: orderStatusTimeSeries,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Order Status Over Time',
                        font: { size: 16 }
                    },
                    legend: {
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (context) => {
                                return `${context.dataset.label}: ${context.parsed.y}`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            precision: 0
                        }
                    }
                },
                interaction: {
                    mode: 'index',
                    intersect: false
                }
            }
        });
    }, [orderStatusTimeSeries]);

    // Prepare order distribution chart configuration
    useEffect(() => {
        // Only create chart config if there's data
        if (!orderMetrics || !orderMetrics.byStatus) {
            setOrderDistributionChartConfig(null);
            return;
        }

        const totalOrders = orderMetrics.byStatus.open + orderMetrics.byStatus.partial + 
                           orderMetrics.byStatus.shipped + orderMetrics.byStatus.cancelled;

        if (totalOrders === 0) {
            setOrderDistributionChartConfig(null);
            return;
        }

        setOrderDistributionChartConfig({
            type: 'doughnut',
            data: {
                labels: ['Not Shipped', 'Partial Shipped', 'Fully Shipped', 'Cancelled'],
                datasets: [{
                    data: [
                        orderMetrics.byStatus.open,
                        orderMetrics.byStatus.partial,
                        orderMetrics.byStatus.shipped,
                        orderMetrics.byStatus.cancelled
                    ],
                    backgroundColor: [
                        '#f98f16ff',  // Orange for Not Shipped
                        '#f5e50bff',  // Yellow for Partial
                        '#10B981',  // Green for Fully Shipped
                        '#EF4444'   // Red for Cancelled
                    ],
                    borderWidth: 1,
                    borderColor: '#fff'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Order Distribution by Status',
                        font: { size: 16 }
                    },
                    legend: {
                        display: true,
                        position: 'right'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                                return `${context.label}: ${context.parsed} (${percentage}%)`;
                            }
                        }
                    }
                }
            }
        });
    }, [orderMetrics]);

    const exportSections = [
        { key: 'orders', label: 'All Orders' },
        { key: 'topStyles', label: 'Top Styles by Revenue' },
        { key: 'topCollections', label: 'Top Collections by Revenue' }
    ];

    const handleExport = (format, sectionKey) => {
        const dateLabel = range === 'ytd' ? 'YTD' : `${range}d`;

        // Orders section
        const orderHeaders = ['Order ID', 'Customer', 'Amount', 'Amount Shipped', 'Status', 'Date', 'Sales Rep'];
        const orderRowMapper = (order) => [
            order.id || order.order_id || '',
            order.customer_name || order.customerName || '',
            `$${(parseFloat(order.amount || order.total || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            `$${(parseFloat(order.amount_shipped || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            getOrderStatus(order) || '',
            order.date_internal || order.creation_time || '',
            Array.isArray(order.commissions_displayNames) ? order.commissions_displayNames.join(', ') : ''
        ];

        // Top Styles section
        const styleHeaders = ['Rank', 'Style', 'Revenue', 'Quantity', '% of Total', 'Orders'];
        const styles = repProductAnalytics?.topStyles || [];
        const styleRowMapper = (s, i) => [
            i + 1,
            s.style || '',
            `$${(s.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            s.quantity || 0,
            `${(s.percentage || 0).toFixed(1)}%`,
            s.orderCount || 0
        ];

        // Top Collections section
        const collectionHeaders = ['Rank', 'Collection', 'Revenue', 'Quantity', '% of Total', 'Orders'];
        const collections = repProductAnalytics?.topCollections || [];
        const collectionRowMapper = (c, i) => [
            i + 1,
            c.collection || '',
            `$${(c.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            c.quantity || 0,
            `${(c.percentage || 0).toFixed(1)}%`,
            c.orderCount || 0
        ];

        if (sectionKey === 'orders') {
            exportData({
                filename: `order_reports_orders_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: orderHeaders, data: nonQuoteOrders, rowMapper: orderRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `All Orders (${dateLabel}) - ${nonQuoteOrders.length} orders`, fontSize: 7, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Orders', columnWidths: [15, 25, 15, 15, 12, 12, 20] }
            });
        } else if (sectionKey === 'topStyles') {
            exportData({
                filename: `order_reports_top_styles_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: styleHeaders, data: styles, rowMapper: styleRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Top Styles by Revenue (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Top Styles', columnWidths: [8, 20, 18, 12, 12, 10] }
            });
        } else if (sectionKey === 'topCollections') {
            exportData({
                filename: `order_reports_top_collections_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: collectionHeaders, data: collections, rowMapper: collectionRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Top Collections by Revenue (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Top Collections', columnWidths: [8, 20, 18, 12, 12, 10] }
            });
        } else {
            // "all" — multi-section export
            exportMultiSectionData({
                filename: `order_reports_all_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format,
                sections: [
                    { name: 'All Orders', headers: orderHeaders, data: nonQuoteOrders, rowMapper: orderRowMapper, columnWidths: [15, 25, 15, 15, 12, 12, 20] },
                    { name: 'Top Styles by Revenue', headers: styleHeaders, data: styles, rowMapper: styleRowMapper, columnWidths: [8, 20, 18, 12, 12, 10] },
                    { name: 'Top Collections by Revenue', headers: collectionHeaders, data: collections, rowMapper: collectionRowMapper, columnWidths: [8, 20, 18, 12, 12, 10] }
                ],
                options: { title: `Order Reports (${dateLabel})`, fontSize: 7, orientation: 'landscape', pageSize: 'a4' }
            });
        }
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading order reports...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            
            {/* Cache Refresh Progress Overlay */}
            {cacheRefreshing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mb-4">
                                <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Refreshing Cache</h3>
                            <p className="text-gray-600 mb-4">
                                Generating reports for all date ranges...
                            </p>
                            <div className="mb-2">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-1000" 
                                        style={{ width: `${refreshProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">
                                {refreshProgress < 30 
                                    ? `${refreshProgress}s - Fetching orders from Firestore...` 
                                    : refreshProgress < 60
                                    ? `${refreshProgress}s - Processing order data...`
                                    : refreshProgress < 90
                                    ? `${refreshProgress}s - Calculating analytics and generating cache...`
                                    : 'Finalizing cache generation...'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Order Reports</h2>
                    <p className="text-gray-600">Track order status, shipment progress, and revenue trends</p>
                </div>
                {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={loading || nonQuoteOrders.length === 0}
                        tooltipText="Export order report data"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        sections={exportSections}
                    />
                )}
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-wrap gap-4 items-start">
                    {/* Sales Rep Filter */}
                    {allReps.length > 0 && (
                        <div className="w-full sm:w-48 flex-shrink-0">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Sales Rep
                            </label>
                            <select
                                value={pendingRep}
                                onChange={(e) => {
                                    setPendingRep(e.target.value);
                                    // Parent-child: clear customer selection when rep changes
                                    setPendingcustomers([]);
                                    setcustomerSearch('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">All Sales Reps</option>
                                {allReps.map(rep => (
                                    <option key={rep.name} value={rep.name}>
                                        {rep.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Customer Filter - Show whenever customers are available (RBAC handled in availablecustomers memo) */}
                    {availablecustomers.length > 0 && (
                        <div className="w-full sm:w-64 flex-shrink-0 relative" ref={customerDropdownRef}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <Tooltip text={
                                    pendingRep 
                                        ? `Showing customers assigned to ${pendingRep}`
                                        : "Filter product analytics by specific customer"
                                }>
                                    <span>Filter by Customer</span>
                                </Tooltip>
                            </label>
                            
                            {/* Display selected customer or search input */}
                            <div className="relative">
                                {pendingcustomers.length > 0 && pendingcustomers[0] ? (
                                    // Show selected customer
                                    <div className="px-3 py-2 border border-gray-300 rounded-md bg-indigo-50 text-sm font-medium">
                                        {availablecustomers.find(c => String(c.id) === String(pendingcustomers[0]))?.name || 'Unknown'}
                                    </div>
                                ) : (
                                    // Show search dropdown
                                    <>
                                        <input
                                            type="text"
                                            placeholder={`Search customers... (${availablecustomers.length} total)`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                            value={customerSearch}
                                            onChange={(e) => setcustomerSearch(e.target.value)}
                                            onFocus={() => setshowCustomerDropdown(true)}
                                        />
                                        
                                        {/* Dropdown list */}
                                        {showCustomerDropdown && (
                                            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                                {filteredcustomers.length > 0 ? (
                                                    <>
                                                        <div 
                                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-200"
                                                            onClick={() => {
                                                                setPendingcustomers([]);
                                                                setcustomerSearch('');
                                                                setshowCustomerDropdown(false);
                                                            }}
                                                        >
                                                            <span className="font-medium">All customers</span> ({availablecustomers.length})
                                                        </div>
                                                        {filteredcustomers.map(customer => (
                                                            <div
                                                                key={customer.id}
                                                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                                onClick={() => {
                                                                    console.log('[Customer Filter] Selected:', { id: customer.id, name: customer.name });
                                                                    setPendingcustomers([customer.id]);
                                                                    setcustomerSearch('');
                                                                    setshowCustomerDropdown(false);
                                                                }}
                                                            >
                                                                {customer.name}
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        No customers found matching "{customerSearch}"
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Date Filter */}
                    <div className="w-full sm:flex-1 sm:min-w-[250px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Date
                        </label>
                        <DateRangeSelector
                            range={pendingRange}
                            setRange={setPendingRange}
                            customStart={pendingCustomStart}
                            setCustomStart={setPendingCustomStart}
                            customEnd={pendingCustomEnd}
                            setCustomEnd={setPendingCustomEnd}
                            hideClear
                            defaultRange="30"
                        />
                    </div>

                    {/* Apply and Clear All Buttons */}
                    <div className="w-full sm:w-auto flex items-end gap-2 mt-auto">
                        <Tooltip text="Apply the selected filters to update the report">
                            <button
                                onClick={handleApplyFilters}
                                disabled={!filtersChanged}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    filtersChanged
                                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {filtersChanged ? '✓ Apply Filters' : 'Applied'}
                            </button>
                        </Tooltip>
                        {(pendingRep || pendingcustomers.length > 0 || pendingRange !== '30' || pendingCustomStart || pendingCustomEnd) && (
                            <Tooltip text="Clear all filters and return to unfiltered view">
                                <button
                                    onClick={handleClearFilters}
                                    className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                                >
                                    Clear All
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>

{/* Info message when filters are active */}
            {(selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0])) && !loadingRepAnalytics && repProductAnalytics && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <div className="font-semibold text-blue-900 mb-1">Showing Filtered Product Analytics</div>
                            <div className="text-sm text-blue-800">
                                Product data loaded from live database
                                {selectedRep && <> for <strong>1 selected rep</strong> ({selectedRep})</>}
                                {selectedcustomers.length > 0 && selectedcustomers[0] && (
                                    <> and <strong>{customers.find(c => String(c.apparelMagicId) === String(selectedcustomers[0]) || String(c.customerAccountNumber) === String(selectedcustomers[0]))?.customerName || 'selected customer'}</strong></>
                                )}
                                .
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cache indicator */}
            {usingCache && cachedData && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-green-800">
                            Using cached data (last updated {cacheAge}m ago)
                        </span>
                    </div>
                    {hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH) && (
                        <Tooltip text="Refresh the reports cache with latest data. Cache auto-refreshes every 8 hours. May take 30-60 seconds." maxWidth={300} position="left">
                            <button
                                onClick={handleRefreshCache}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            >
                                Refresh Cache
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Order Metrics Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Quotes</div>
                        <div className="text-xs mb-2">Quotes (is_quote) from ApparelMagic in this date range</div>
                        <div className="text-xs text-yellow-200 mb-1">Revenue: ${quoteMetrics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                        <div className="text-xs text-blue-200">Not included in order totals or revenue cards</div>
                    </div>
                } maxWidth={320}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Quotes</div>
                        <div className="text-3xl font-bold text-amber-600">{quoteMetrics.count}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            ${quoteMetrics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text="Total number of orders (excluding quotes) in the selected date range.">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Total Orders</div>
                        <div className="text-3xl font-bold text-blue-600">{orderMetrics.total}</div>
                    </div>
                </Tooltip>              

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Open Orders</div>
                        <div className="text-xs mb-2">Orders not yet fully shipped:</div>
                        <div className="text-xs space-y-1">
                            <div>• Not Shipped: {orderMetrics.byStatus.open}</div>
                            <div>• Partial Shipped: {orderMetrics.byStatus.partial}</div>
                            <div className="mt-2 text-yellow-200">Uses fulfillment_status field when available</div>
                        </div>
                    </div>
                } maxWidth={300}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Open Orders</div>
                        <div className="text-3xl font-bold text-orange-600">{orderMetrics.totalOpenOrders}</div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Fully Shipped Orders</div>
                        <div className="text-xs mb-1">Orders with all items shipped</div>
                        <div className="text-xs text-yellow-200">Status: Shipped, Delivered, or Complete</div>
                    </div>
                } maxWidth={300}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Fully Shipped</div>
                        <div className="text-3xl font-bold text-green-600">{orderMetrics.byStatus.shipped}</div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Cancelled / Returned Orders</div>
                        <div className="text-xs mb-1">Orders with cancelled or returned status</div>
                        <div className="text-xs text-yellow-200">Includes both Cancelled and Returned orders</div>
                    </div>
                } maxWidth={270}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Cancelled / Returned</div>
                        <div className="text-3xl font-bold text-red-600">{orderMetrics.byStatus.cancelled}</div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Total Revenue</div>
                        <div className="text-xs mb-2">Sum of order-level amounts from ApparelMagic</div>
                        <div className="text-xs text-yellow-200 mb-2"><strong>Includes</strong> all items and misc charges</div>
                    </div>
                } maxWidth={360}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
                        <div className="text-3xl font-bold text-green-600">
                            ${orderMetrics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Total Item Revenue</div>
                        <div className="text-xs mb-2">Sum of all order line items (price × quantity)</div>
                        <div className="text-xs text-yellow-200 mb-2"><strong>Calculation:</strong> Σ(item price × item qty)</div>
                        <div className="text-xs text-purple-200">Excludes misc_charges (embroidery, freight, etc.)</div>
                    </div>
                } maxWidth={360}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="flex items-center gap-2">
                            <div className="text-sm text-gray-600 mb-1">Total Item Revenue</div>
                        </div>
                        <div className="text-3xl font-bold text-green-600">
                            ${orderMetrics.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Average Order Revenue</div>
                        <div className="text-xs mb-2">Average revenue per order across all orders in the selected period</div>
                        <div className="text-xs text-yellow-200 mb-1">Calculation: Total Item Revenue ÷ Number of Orders</div>
                        <div className="text-xs text-blue-200 italic">Excludes misc_charges</div>
                    </div>
                } maxWidth={320}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Avg Order Revenue</div>
                        <div className="text-3xl font-bold text-purple-600">
                            ${orderMetrics.avgOrderValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold mb-1">Total Items</div>
                        <div className="text-xs mb-2">Total units (qty) across all order line items in the selected period</div>
                        <div className="text-xs text-yellow-200 mb-1">Calculation: Σ(item qty) across non-quote orders</div>
                        <div className="text-xs text-blue-200 italic">Excludes misc_charges (embroidery, freight, etc.)</div>
                    </div>
                } maxWidth={320}>
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Total Items</div>
                        <div className="text-3xl font-bold text-teal-600">
                            {(orderMetrics.totalItems || 0).toLocaleString('en-US')}
                        </div>
                    </div>
                </Tooltip>
            </div>

            {/* Recent Orders Table (Filtered by Current Date Selection) */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Top 10 Orders by $ Amount</h2>
                </div>
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                <th className="px-3 py-3 whitespace-nowrap">Order #</th>
                                <th className="px-3 py-3">Customer</th>
                                <th className="px-3 py-3 whitespace-nowrap">Order Date</th>
                                <th className="px-3 py-3 whitespace-nowrap">Amount</th>
                                <th className="px-3 py-3 whitespace-nowrap text-center">Items</th>
                                <th className="px-3 py-3 whitespace-nowrap">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8">
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="text-gray-500">Loading orders...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : recentOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-gray-500">
                                        No recent orders found.
                                    </td>
                                </tr>
                            ) : (
                                <>
                                {recentOrders.map((order, index) => {
                                    // Customer lookup - use cached customer_name or fallback to customers array
                                    // Link by customerAccountNumber (enriched field from sync) per schema
                                    const customerName = order.customer_name || 
                                        customers.find(c => String(c.customerAccountNumber) === String(order.customerAccountNumber))?.customerName || 
                                        'Unknown Customer';
                                    
                                    // Order number - use id field (available in both cache and live data)
                                    const orderNumber = order.id || '-';
                                    
                                    // Amount - cache uses 'total', live data uses 'amount'
                                    const amount = order.total || order.amount || 0;
                                    
                                    // Items count - cache has pre-calculated item_count (currently 0 to save memory), live data needs calculation.
                                    // Note: If both item_count is 0 AND no order_items array, show "-" (cache optimization)
                                    const itemCount = getOrderItemCount(order);
                                    
                                    // Date - prioritize date_internal (matches filter) over creation_time
                                    // date_internal = order scheduled/due date (what filter uses)
                                    // creation_time = when order was created in ApparelMagic
                                    let orderDate = '-';
                                    if (order.date_internal) {
                                        // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
                                        // new Date('2026-03-01') = UTC midnight = Feb 28 in US timezones
                                        const [y, m, d] = order.date_internal.split('-').map(Number);
                                        orderDate = new Date(y, m - 1, d).toLocaleDateString();
                                    } else if (order.creation_time) {
                                        if (typeof order.creation_time === 'string') {
                                            orderDate = new Date(order.creation_time.replace(' ', 'T')).toLocaleDateString();
                                        } else if (typeof order.creation_time === 'number') {
                                            orderDate = new Date(order.creation_time).toLocaleDateString();
                                        }
                                    } else if (order.date) {
                                        orderDate = new Date(order.date).toLocaleDateString();
                                    }
                                    
                                    return (
                                        <tr key={`order-${order.id || order.customer_id}-${index}`} className="text-gray-700 hover:bg-gray-50">
                                            <td className="px-3 py-3 whitespace-nowrap">
                                                {order.id ? (
                                                    <Link
                                                        to={`/order/${order.id}`}
                                                        className="text-indigo-600 hover:underline font-mono"
                                                    >
                                                        {orderNumber}
                                                    </Link>
                                                ) : (
                                                    <span className="text-gray-500 font-mono">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 max-w-[200px] truncate" title={customerName}>
                                                {customerName}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap">
                                                {orderDate}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap">
                                                ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-3 py-3 text-center">
                                                {itemCount || '-'}
                                            </td>
                                            <td className="px-3 py-3">
                                                {isQuoteOrder(order) ? (
                                                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                                                        Quote
                                                    </span>
                                                ) : (
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        order.fulfillment_status === 'Shipped' ? 'bg-green-100 text-green-700' :
                                                        order.fulfillment_status === 'Cancelled' || order.fulfillment_status === 'Returned' ? 'bg-red-100 text-red-700' :
                                                        order.fulfillment_status === 'Complete' ? 'bg-blue-100 text-blue-700' :
                                                        'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                        {order.fulfillment_status || 'Pending'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {topOrdersOtherSummary && (
                                    <tr
                                        className="bg-gray-50 text-gray-700 border-t-2 border-gray-300 font-semibold"
                                        title="Aggregated amount and units for all orders ranked outside the top 10, so the totals here reconcile with the summary cards above. Quotes are excluded."
                                    >
                                        <td className="px-3 py-3 whitespace-nowrap text-gray-500">—</td>
                                        <td className="px-3 py-3" colSpan="2">
                                            Other ({topOrdersOtherSummary.orderCount.toLocaleString()} order{topOrdersOtherSummary.orderCount === 1 ? '' : 's'})
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            ${topOrdersOtherSummary.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            {topOrdersOtherSummary.itemCount ? topOrdersOtherSummary.itemCount.toLocaleString() : '-'}
                                        </td>
                                        <td className="px-3 py-3"></td>
                                    </tr>
                                )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Order Status Over Time */}
            <div className="mb-8">
            {orderStatusChartConfig ? (
                <CompactChart
                    chartConfig={orderStatusChartConfig}
                    title="Orders vs Shipped vs Partial vs Cancelled"
                    height="h-96"
                    tooltip={
                        <div className="text-left">
                            <div className="font-semibold mb-1">Order Status Tracking</div>
                            <div className="text-xs mb-2">Track orders by fulfillment status over time:</div>
                            <div className="text-xs space-y-1">
                                <div><strong>Open:</strong> Not yet shipped (amount_shipped = 0)</div>
                                <div><strong>Partial:</strong> Some items shipped, some pending (both fields &gt; 0)</div>
                                <div><strong>Shipped:</strong> All items shipped (amount_open = 0)</div>
                                <div><strong>Cancelled:</strong> Order cancelled</div>
                            </div>
                        </div>
                    }
                    tooltipMaxWidth={400}
                />
            ) : (
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Orders vs Shipped vs Partial vs Cancelled</h3>
                    <div className="h-96 flex items-center justify-center">
                        <div className="text-gray-500 text-center">
                            <div className="text-4xl mb-2">📦</div>
                            <div>No order data available</div>
                            <div className="text-sm mt-1">Orders will appear here once created</div>
                        </div>
                    </div>
                </div>
            )}
            </div>
            
            {/* Order Status Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {orderDistributionChartConfig ? (
                    <CompactChart
                        chartConfig={orderDistributionChartConfig}
                        title="Order Status Distribution"
                        height="h-64"
                        tooltip="Distribution of orders by current fulfillment status. Visualizes what percentage of orders are in each state."
                    />
                ) : (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Order Status Distribution</h3>
                        <div className="h-64 flex items-center justify-center">
                            <div className="text-gray-500 text-center">
                                <div className="text-4xl mb-2">📊</div>
                                <div>No order data available</div>
                                <div className="text-sm mt-1">Orders will appear here once created</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Order Status Summary</h3>
                    
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                                <span className="font-medium text-gray-900">Not Shipped</span>
                            </div>
                            <div className="text-xl font-bold text-orange-600">{orderMetrics.byStatus.open}</div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded">
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                                <span className="font-medium text-gray-900">Partial Shipped</span>
                            </div>
                            <div className="text-xl font-bold text-yellow-600">{orderMetrics.byStatus.partial}</div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                                <span className="font-medium text-gray-900">Fully Shipped</span>
                            </div>
                            <div className="text-xl font-bold text-green-600">{orderMetrics.byStatus.shipped}</div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-red-50 rounded">
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                                <span className="font-medium text-gray-900">Cancelled</span>
                            </div>
                            <div className="text-xl font-bold text-red-600">{orderMetrics.byStatus.cancelled}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading message for analytics */}
            {(selectedRep || (selectedcustomers.length > 0 && selectedcustomers[0])) && loadingRepAnalytics && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <svg className="animate-spin h-5 w-5 text-yellow-600 mr-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-yellow-800">
                            Loading product analytics...
                        </span>
                    </div>
                </div>
            )}

            {/* Product Analytics Section - Show cached data OR API-fetched filtered data */}
            {/* CRITICAL FIX: Show analytics for all users, but use cached data only for admins */}
            {(() => {
                // Determine what data to show
                const hasFilters = selectedRep || selectedcustomers[0];
                const isAdmin = canViewAllData(user);
                
                // Priority:
                // 1. If API-fetched data available (filters or custom date range): use repProductAnalytics
                // 2. If admin with cache: use cachedData
                // 3. Fallback: empty analytics
                // NOTE: repProductAnalytics is cleared at start of every fetchData(),
                // so it's only truthy when the current fetch set it (API path).
                const analyticsData = repProductAnalytics
                    ? repProductAnalytics 
                    : (isAdmin && usingCache && cachedData 
                        ? cachedData 
                        : { 
                            topStyles: [], 
                            topCollections: []
                        });
                
                if (!analyticsData) {
                    // If filters are active but no data loaded, show message
                    if (hasFilters) {
                        return (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                                <p className="text-yellow-800">
                                    Unable to load filtered product analytics. Please ensure the server is running.
                                </p>
                                <p className="text-sm text-yellow-600 mt-2">
                                    Run: <code className="bg-yellow-100 px-2 py-1 rounded">npm run server</code>
                                </p>
                            </div>
                        );
                    }
                    return null;
                }
                
                return (
                <>
                    {/* Order Details Debug Info - Show when filters active */}
                    {hasFilters && repProductAnalytics && (
                        <div className="bg-white p-6 rounded-lg shadow mb-6">
                            <details className="cursor-pointer">
                                <summary className="text-lg font-bold text-gray-900 hover:text-indigo-600">
                                    📋 View Order Details Used in Calculations ({rawOrdersData ? rawOrdersData.length : 0})
                                </summary>
                                <div className="mt-4 overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer ID</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                                    <Tooltip text="Number of unique line items (SKUs) on the order" position="top">
                                                        <span>Line Items</span>
                                                    </Tooltip>
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                                    <Tooltip text="Total quantity of all items (sum of all units ordered)" position="top">
                                                        <span>Total Qty</span>
                                                    </Tooltip>
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Amount</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sales Reps</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {rawOrdersData && rawOrdersData.length > 0 ? (
                                                rawOrdersData.map((order) => {
                                                    const totalQty = order.order_items?.reduce((sum, item) => {
                                                        return sum + parseInt(item.qty || item.quantity || 0, 10);
                                                    }, 0) || 0;
                                                    
                                                    return (
                                                        <tr key={order.id}>
                                                            <td className="px-3 py-2 text-sm font-mono">{order.id}</td>
                                                            <td className="px-3 py-2 text-sm">{order.customer_id}</td>
                                                            <td className="px-3 py-2 text-sm text-right">{order.order_items?.length || 0}</td>
                                                            <td className="px-3 py-2 text-sm text-right">{totalQty}</td>
                                                            <td className="px-3 py-2 text-sm text-right">${(order.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                            <td className="px-3 py-2 text-sm text-xs">{order.commissions_displayNames?.join(', ') || 'N/A'}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="px-3 py-4 text-center text-gray-500">
                                                        {hasFilters ? 'No order data available. Try adjusting your filters.' : 'Apply filters to view order details.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <p className="text-xs text-gray-500 mt-3">
                                        <strong>Note:</strong> These are the exact orders used to calculate the product analytics above.
                                    </p>
                                </div>
                            </details>
                        </div>
                    )}
                    
                    {/* Top Styles */}
                    <div className="bg-white p-6 rounded-lg shadow mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-xl font-bold text-gray-900">🏆 Top 10 Styles by Revenue</h3>
                            <Tooltip text={
                                <div className="text-left">
                                    <div className="font-semibold text-yellow-300 mb-2">⚠️ Style Analytics</div>
                                    <div className="text-xs text-blue-200 mb-2"><strong>% of Total</strong> = (Style Revenue) / (Total Item Revenue)</div>
                                    <div className="text-xs text-green-200">Totals match Total Item Revenue card</div>
                                </div>
                            } maxWidth={360}>
                                <svg className="w-5 h-5 text-blue-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </Tooltip>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Style</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders*</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {analyticsData.topStyles && analyticsData.topStyles.length > 0 ? (
                                        <>
                                            {analyticsData.topStyles.map((item, idx) => (
                                                <tr key={idx} className={idx < 3 ? 'bg-yellow-50' : ''}>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{idx + 1}</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.style}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-900">${item.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-600">{item.quantity.toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-indigo-600 font-medium">{item.percentage.toFixed(1)}%</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-600">{item.orderCount.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                            <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                                                <td className="px-4 py-3 text-sm text-gray-900" colSpan="2">Total (Top 10)</td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    ${analyticsData.topStyles.reduce((sum, item) => sum + item.revenue, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    {analyticsData.topStyles.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-indigo-700 font-bold">
                                                    {analyticsData.topStyles.reduce((sum, item) => sum + item.percentage, 0).toFixed(1)}%
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-600" title="Unique orders containing these top 10 styles">
                                                    {(() => {
                                                        const uniqueOrders = new Set();
                                                        // Note: We can't calculate unique orders from aggregated data
                                                        // This shows sum which may count same order multiple times
                                                        return analyticsData.topStyles.reduce((sum, item) => sum + item.orderCount, 0);
                                                    })().toLocaleString()}
                                                    <span className="text-xs text-gray-500">*</span>
                                                </td>
                                            </tr>
                                        </>
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                No style data available for this period
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            * Orders column shows sum of order counts per row. Same order may appear in multiple rows if it contains multiple styles/colors.
                        </p>
                    </div>

                    {/* Top Colors */}
                    <div className="bg-white p-6 rounded-lg shadow mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-xl font-bold text-gray-900">🎨 Top 10 Collections by Revenue</h3>
                            <Tooltip text={
                                <div className="text-left">
                                    <div className="font-semibold text-yellow-300 mb-2">⚠️ Collection Analytics</div>
                                    <div className="text-xs text-blue-200 mb-2"><strong>% of Total</strong> = (Collection Revenue) / (Total Item Revenue)</div>
                                    <div className="text-xs text-green-200">Totals match Total Item Revenue card</div>
                                </div>
                            } maxWidth={360}>
                                <svg className="w-5 h-5 text-blue-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </Tooltip>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {analyticsData.topCollections && analyticsData.topCollections.length > 0 ? (
                                        <>
                                            {analyticsData.topCollections.map((item, idx) => (
                                                <tr key={idx} className={idx < 3 ? 'bg-blue-50' : ''}>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{idx + 1}</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.collection}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-900">${item.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-600">{item.quantity.toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-sm text-right text-indigo-600 font-medium">{item.percentage.toFixed(1)}%</td>
                                                    <td className="px-4 py-3 text-sm text-right text-gray-600">{item.orderCount.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                            <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                                                <td className="px-4 py-3 text-sm text-gray-900" colSpan="2">Total (Top 10)</td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    ${analyticsData.topCollections.reduce((sum, item) => sum + item.revenue, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-900">
                                                    {analyticsData.topCollections.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-indigo-700 font-bold">
                                                    {analyticsData.topCollections.reduce((sum, item) => sum + item.percentage, 0).toFixed(1)}%
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right text-gray-600" title="Sum of order counts - may include same order multiple times">
                                                    {analyticsData.topCollections.reduce((sum, item) => sum + item.orderCount, 0).toLocaleString()}
                                                    <span className="text-xs text-gray-500">*</span>
                                                </td>
                                            </tr>
                                        </>
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                No collection data available for this period
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
                );
            })()}
        </main>
    );
};

export default OrderReportsPage;









